/* eslint-disable @typescript-eslint/no-explicit-any */
import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6 Vendor-Port W4 — PLATE HEAT EXCHANGER selector data layer (§33.4).
 *
 * The PHX selector's only WRITES are:
 *   1. RATE  → POST /api/v1.0/rating/ {part:'plate_heat_exchanger', ...} — the
 *      EXTERNAL vendor dispatch (Recuperator/Heatex), handled by the SHARED
 *      useRatingDispatch (ratingApi). NOT re-declared here.
 *   2. SELECT (apply) → current_model.set(models_field) (the component auto-save
 *      PATCH ahu/plate_heat_exchangers/<id>) + self.model.set(rating_fields) on
 *      the rated_phx_model (PATCH ahu/plate_heat_exchangers/rating_mode/<id> —
 *      patchRatedPhx, already in ahuComponentFormsApi). Both routed through
 *      existing mutations; this file declares only the typed READS the selector
 *      needs that aren't already in ahuComponentFormsApi.
 *
 * ★ DATA-SAFETY: every endpoint here is a GET. The mutating paths (Select)
 * route through existing component auto-save + patchRatedPhx (userArmed-gated in
 * the selector). The read-only SMOKE fires GETs only → 0 mutating + 0 /rating/.
 */

export interface PhxNote {
  note: string;
  [key: string]: unknown;
}

export const phxSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // phx rating notes (legacy NotesCollection).
    getPhxNotes: builder.query<PhxNote[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): PhxNote[] => {
        if (Array.isArray(res)) return res as PhxNote[];
        const o = res as { notes?: PhxNote[]; results?: PhxNote[] } | null;
        return o?.notes ?? o?.results ?? [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // mfg settings (legacy getMfgSettings, 2249-2257). Plain JSON map keyed by
    // mfg id; read for max/min face velocity + generic_mfg per mfg.
    getPhxMfgSettings: builder.query<Record<string, unknown>, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // module configurations (legacy getModuleConfigurations, 2258-2266).
    getPhxModuleConfigs: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] =>
        Array.isArray(res) ? res : ((res as any)?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // casing height (legacy getCasingHeight, 2236-2248) — URL carries mfg/series
    // (+optional size) as query params; URL-keyed cache.
    getPhxCasingHeight: builder.query<any, string>({
      query: (url) => url,
    }),

    // sizes (legacy getSize, 2267-2279) — URL carries mfg/series path segments.
    getPhxSizes: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] =>
        Array.isArray(res) ? res : ((res as any)?.results ?? []),
    }),

    // damper shaft master lists (legacy DamperShaft.ShaftShape/Length/Diameter).
    // shape/diameter/external_length — read for the shaft datasets at select.
    getPhxDamperShaft: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] =>
        Array.isArray(res) ? res : ((res as any)?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPhxNotesQuery,
  useGetPhxMfgSettingsQuery,
  useGetPhxModuleConfigsQuery,
  useGetPhxCasingHeightQuery,
  useGetPhxSizesQuery,
  useGetPhxDamperShaftQuery,
} = phxSelectorApi;
